import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useLazyGetAvailableRoomsQuery } from "../pages/booking/apiSlice";
import LoadingAnimation from "./LoadingAnimation";

const FieldLabel = ({ children, number }) => (
  <div className="mb-3 flex items-center gap-3">
    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#E0B973]/20 font-montserrat text-[9px] font-bold text-[#9f7a36]">{number}</span>
    <span className="font-montserrat text-[9px] font-bold uppercase tracking-[.2em] text-[#14274A]/55">{children}</span>
  </div>
);

const SelectField = ({ id, value, onChange, children }) => (
  <div className="relative">
    <select id={id} value={value} onChange={onChange} className="reservation-control appearance-none pr-9">{children}</select>
    <svg className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9f7a36]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="m6 9 6 6 6-6" /></svg>
  </div>
);

const BookingForm = () => {
  const navigate = useNavigate();
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [guestType, setGuestType] = useState("local");
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [errors, setErrors] = useState([]);
  const [trigger, { isLoading }] = useLazyGetAvailableRoomsQuery();

  const handleSubmit = async (event) => {
    event.preventDefault();
    const nextErrors = [];
    if (!checkIn) nextErrors.push("Choose your arrival date.");
    if (!checkOut) nextErrors.push("Choose your departure date.");
    if (checkIn && checkOut && checkIn >= checkOut) nextErrors.push("Departure must be after arrival.");
    if (nextErrors.length) { setErrors(nextErrors); return; }

    setErrors([]);
    const offset = new Date().getTimezoneOffset() * 60000;
    const formatLocal = (date) => new Date(date.getTime() - offset).toISOString().split("T")[0];
    const checkInValue = formatLocal(checkIn);
    const checkOutValue = formatLocal(checkOut);

    try {
      const result = await trigger({ checkIn: checkInValue, checkOut: checkOutValue }).unwrap();
      localStorage.setItem("rooms", JSON.stringify(result));
      navigate("/roomtype", { state: { checkIn: checkInValue, checkOut: checkOutValue, guestType, adults, children } });
    } catch (_) {
      setErrors(["We couldn’t check availability right now. Please try again."]);
    }
  };

  if (isLoading) return <LoadingAnimation />;

  return (
    <motion.div initial={{ opacity: 0, x: 35 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: .85, delay: .15, ease: [0.16, 1, 0.3, 1] }} className="relative w-full bg-[#FCFAF6] shadow-[0_35px_100px_rgba(4,13,30,.35)]">
      <div className="absolute left-0 top-0 h-1 w-full bg-gradient-to-r from-[#E0B973] via-[#f2d99e] to-[#E0B973]" />
      <form onSubmit={handleSubmit} className="p-7 sm:p-10 lg:p-12">
        <div className="mb-9 flex items-start justify-between gap-5 border-b border-[#14274A]/10 pb-8">
          <div><p className="section-eyebrow !mb-2">Direct reservation</p><h2 className="font-garamond text-4xl font-medium leading-none text-[#14274A] sm:text-5xl">Find your room</h2></div>
          <span className="hidden h-12 w-12 items-center justify-center rounded-full border border-[#E0B973]/50 font-garamond text-xl italic text-[#9f7a36] sm:flex">Z</span>
        </div>

        <div className="grid gap-7 sm:grid-cols-2">
          <div><FieldLabel number="1">Arrival</FieldLabel><DatePicker selected={checkIn} onChange={(date) => { setCheckIn(date); if (checkOut && date >= checkOut) setCheckOut(null); }} selectsStart startDate={checkIn} endDate={checkOut} minDate={new Date()} placeholderText="Select check-in" dateFormat="dd MMM yyyy" className="reservation-control" /></div>
          <div><FieldLabel number="2">Departure</FieldLabel><DatePicker selected={checkOut} onChange={setCheckOut} selectsEnd startDate={checkIn} endDate={checkOut} minDate={checkIn || new Date()} placeholderText="Select check-out" dateFormat="dd MMM yyyy" className="reservation-control" /></div>
        </div>

        <div className="my-8 flex items-center gap-4"><span className="h-px flex-1 bg-[#14274A]/10" /><span className="font-montserrat text-[8px] uppercase tracking-[.22em] text-[#14274A]/35">Guest details</span><span className="h-px flex-1 bg-[#14274A]/10" /></div>

        <div className="grid gap-6 sm:grid-cols-3">
          <div><FieldLabel number="3">Guest type</FieldLabel><SelectField id="guestType" value={guestType} onChange={(e) => setGuestType(e.target.value)}><option value="local">Bhutanese</option><option value="foreigner">International</option></SelectField></div>
          <div><FieldLabel number="4">Adults</FieldLabel><SelectField id="adults" value={adults} onChange={(e) => setAdults(Number(e.target.value))}>{[...Array(10)].map((_, i) => <option key={i + 1} value={i + 1}>{i + 1} {i === 0 ? "adult" : "adults"}</option>)}</SelectField></div>
          <div><FieldLabel number="5">Children</FieldLabel><SelectField id="children" value={children} onChange={(e) => setChildren(Number(e.target.value))}>{[...Array(10)].map((_, i) => <option key={i} value={i}>{i} {i === 1 ? "child" : "children"}</option>)}</SelectField></div>
        </div>

        <AnimatePresence>
          {errors.length > 0 && <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="mt-7 border-l-2 border-red-400 bg-red-50 px-4 py-3 font-montserrat text-xs leading-6 text-red-700">{errors.map((error) => <p key={error}>{error}</p>)}</motion.div>}
        </AnimatePresence>

        {guestType === "foreigner" && <p className="mt-6 rounded-sm bg-[#E0B973]/15 px-4 py-3 font-montserrat text-xs leading-5 text-[#14274A]/70">International online booking is temporarily unavailable. Please contact our team for personal assistance.</p>}

        <button type="submit" disabled={guestType === "foreigner"} className="mt-8 flex h-14 w-full items-center justify-center gap-3 bg-[#14274A] font-montserrat text-[10px] font-bold uppercase tracking-[.22em] text-white transition hover:bg-[#203964] disabled:cursor-not-allowed disabled:bg-[#14274A]/35">Check availability <span className="text-[#E0B973]">→</span></button>

        <div className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 font-montserrat text-[8px] uppercase tracking-[.14em] text-[#14274A]/42"><span>✓ Breakfast included</span><span>✓ Secure booking</span><span>✓ Direct support</span></div>
      </form>
    </motion.div>
  );
};

export default BookingForm;
